import React, { Dispatch, memo, SetStateAction, useCallback, useState } from 'react';

import { Address } from '@screens/PlacingOrder/ui/Address/Address';
import { DeliveryService } from '@screens/PlacingOrder/ui/DeliveryService/DeliveryService';
import { usePersonalAccountFields } from '@shared/hooks/usePersonalAccountFields';
import { CdekOffice } from '@shared/types/cdek';
import { DeliveryCompany } from '@shared/types/enums/delivery.enum';
import { VStack } from '@shared/ui/Stack';

import { CdekWidget } from '../CdekWidget/CdekWidget';
import { Contacts } from '../Contacts/Contacts';
import { ManagerCall } from '../ManagerCall/ManagerCall';
import { PaymentMethods } from '../PaymentMethods/PaymentMethods';
import cls from './Form.module.scss';

type FormProps = { onClickPaymentMethodTab: (tab: string) => void } & Omit<
  ReturnType<typeof usePersonalAccountFields>,
  | 'disabledButton'
  | 'setErrorFirstName'
  | 'setErrorLastName'
  | 'setErrorEmail'
  | 'setErrorPhoneNumber'
  | 'setErrorPostalCode'
  | 'setErrorRegion'
  | 'setErrorCity'
  | 'setErrorStreet'
  | 'setErrorHouseNumber'
> & {
    totalAmount: number;
    deliveryCompany: DeliveryCompany;
    onChangeDeliveryCompany: (tab: string) => void;
    onChangeSelectedPoint: (point?: CdekOffice) => void;
    onClickManagerCallTab: (tab: string) => void;
    setErrorDeliveryPoint: Dispatch<SetStateAction<string>>;
    selectedPoint?: CdekOffice;
    errorDeliveryPoint?: string;
  };

export const Form = memo<FormProps>(
  ({
    totalAmount,
    secondName,
    firstName,
    lastName,
    phoneNumber,
    email,
    postalCode,
    region,
    city,
    street,
    houseNumber,
    corpus,
    building,
    apartment,
    floor,
    comment,
    errorFirstName,
    errorLastName,
    errorEmail,
    errorPostalCode,
    errorRegion,
    errorCity,
    errorHouseNumber,
    errorStreet,
    selectedPoint,
    deliveryCompany,
    errorDeliveryPoint,
    handleChangeLastName,
    handleChangeFirstName,
    handleChangeSecondName,
    handleChangePhoneNumber,
    handleChangeEmail,
    handleChangePostalCode,
    handleChangeRegion,
    handleChangeCity,
    handleChangeStreet,
    handleChangeHouseNumber,
    handleChangeCorpus,
    handleChangeBuilding,
    handleChangeApartment,
    handleChangeFloor,
    handleChangeComment,
    onClickPaymentMethodTab,
    onChangeSelectedPoint,
    onChangeDeliveryCompany,
    onClickManagerCallTab,
    setErrorDeliveryPoint
  }) => {
    const [openCdekWidget, setOpenCdekWidget] = useState(false);

    const onClickOpenCdekWidget = useCallback(() => {
      setOpenCdekWidget((prevOpen) => !prevOpen);
    }, []);

    return (
      <VStack className={cls.wrapper}>
        <Contacts
          {...{
            secondName,
            firstName,
            lastName,
            phoneNumber,
            email,
            errorFirstName,
            errorLastName,
            errorEmail,
            handleChangeLastName,
            handleChangeFirstName,
            handleChangeSecondName,
            handleChangePhoneNumber,
            handleChangeEmail
          }}
        />
        <DeliveryService
          deliveryAddress={
            selectedPoint ? `${selectedPoint.location.city}, ${selectedPoint.location.address}` : undefined
          }
          {...{ errorDeliveryPoint, onClickOpenCdekWidget, onChangeDeliveryCompany }}
        />
        {deliveryCompany === DeliveryCompany.BAIKAL && (
          <Address
            {...{
              postalCode,
              region,
              city,
              street,
              houseNumber,
              corpus,
              building,
              apartment,
              floor,
              comment,
              errorPostalCode,
              errorRegion,
              errorCity,
              errorHouseNumber,
              errorStreet,
              handleChangePostalCode,
              handleChangeRegion,
              handleChangeCity,
              handleChangeStreet,
              handleChangeHouseNumber,
              handleChangeCorpus,
              handleChangeBuilding,
              handleChangeApartment,
              handleChangeFloor,
              handleChangeComment
            }}
          />
        )}
        {deliveryCompany !== 'baikal' && <ManagerCall {...{ onClickManagerCallTab }} />}
        <PaymentMethods {...{ totalAmount, onClickPaymentMethodTab }} />
        <div className={cls.requiredText}>* Обязательные поля</div>
        <CdekWidget
          open={openCdekWidget}
          onClose={onClickOpenCdekWidget}
          {...{ selectedPoint, setErrorDeliveryPoint, onChangeSelectedPoint }}
        />
      </VStack>
    );
  }
);
